import { createTheme } from "@mui/material/styles";

const base = {
    spacing: (factor) => `${8 * factor}px`,
    shape: { borderRadius: 4 },
    typography: {
        fontWeightSemiBold: 600,
        size: {
            xxs: "0.625rem",
            xs: "0.6875rem",
            sm: "0.75rem",
            smd: "0.8125rem",
            md: "0.875rem",
            base: "1rem",
            lg: "1.25rem",
            xl: "1.5rem",
            xlm: "1.75rem",
            xxl: "2rem",
            xxxl: "2.25rem"
        },
    },
};

const lightWarning = { main: "#ed6c02", light: "#ff9800", dark: "#e65100" };
const darkWarning = { main: "#ffa726", light: "#ffb74d", dark: "#f57c00" };

const lightStatus = {
    project: {
        "Awaiting Authorization": lightWarning.main,
        Pending: "#9e9e9e",
        "Not Started": "#9e9e9e",
        Planning: "#5c6bc0",
        "In Progress": "#1976d2",
        Active: "#1976d2",
        Testing: "#0097a7",
        "Ready for Review": "#7b1fa2",
        Review: "#7b1fa2",
        Approved: "#43a047",
        Completed: "#6d4c41",
        "On Hold": "#f57c00",
        Cancelled: "#e53935",
    },
    quote: {
        Pending: "#ed6c02",
        Draft: "#9e9e9e",
        "Under Review": "#00897b",
        Sent: "#0288d1",
        Accepted: "#2e7d32",
        Declined: "#d32f2f",
        Rejected: "#c62828",
    },
    invoice: {
        Draft: "#ed6c02",
        Open: "#0288d1",
        Paid: "#2e7d32",
        Overdue: "#d32f2f",
        Cancelled: "#9e9e9e",
    },
    priority: {
        Highest: "#d32f2f",
        High: "#ed6c02",
        Medium: "#0288d1",
        Low: "#2e7d32",
        Lowest: "#66bb6a",
    },
    privacy: {
        Private: "#d32f2f",
        Internal: "#f57c00",
        Public: "#0288d1",
    },
    active: {
        Active: "#2e7d32",
        Paused: "#ed6c02",
        Inactive: "#d32f2f",
    },
    billing: {
        Retainer: "#0288d1",
        Hourly: "#2e7d32",
        "Fixed Price": "#00897b",
        Internal: "#f57c00",
        "Non Billable": "#9e9e9e",
        Parent: "#9e9e9e",
    },
    deadline: {
        "Open Ended": "#0288d1",
        "On Track": "#2e7d32",
        Upcoming: "#ed6c02",
        Overdue: "#d32f2f",
    },
};

const darkStatus = {
    project: {
        "Awaiting Authorization": darkWarning.main,
        Pending: "#bdbdbd",
        "Not Started": "#bdbdbd",
        Planning: "#7986cb",
        "In Progress": "#42a5f5",
        Active: "#42a5f5",
        Testing: "#4dd0e1",
        "Ready for Review": "#ba68c8",
        Approved: "#81c784",
        Completed: "#c19a6b",
        "On Hold": "#ffb74d",
        Review: "#ba68c8",
        Cancelled: "#ef5350",
    },
    quote: {
        Pending: "#ffa726",
        Draft: "#bdbdbd",
        "Under Review": "#4db6ac",
        Sent: "#29b6f6",
        Accepted: "#66bb6a",
        Declined: "#ef5350",
        Rejected: "#e57373",
    },
    invoice: {
        Draft: "#ffa726",
        Open: "#29b6f6",
        Paid: "#66bb6a",
        Overdue: "#ef5350",
        Cancelled: "#bdbdbd",
    },
    priority: {
        Highest: "#ef5350",
        High: "#ffa726",
        Medium: "#29b6f6",
        Low: "#66bb6a",
        Lowest: "#81c784",
    },
    privacy: {
        Private: "#ef5350",
        Internal: "#ffb74d",
        Public: "#29b6f6",
    },
    active: {
        Active: "#66bb6a",
        Paused: "#ffa726",
        Inactive: "#ef5350",
    },
    billing: {
        Retainer: "#29b6f6",
        Hourly: "#66bb6a",
        "Fixed Price": "#4db6ac",
        Internal: "#ffb74d",
        "Non Billable": "#bdbdbd",
        Parent: "#bdbdbd",
    },
    deadline: {
        "Open Ended": "#29b6f6",
        "On Track": "#66bb6a",
        Upcoming: "#ffa726",
        Overdue: "#ef5350",
    },
};

export const lightTheme = createTheme({
    ...base,
    palette: {
        mode: "light",
        primary: { main: "#1976d2", light: "#42a5f5", dark: "#1565c0", contrastText: "#ffffff" },
        secondary: { main: "#00897b", light: "#4db6ac", dark: "#00695f", contrastText: "#ffffff" },
        error: { main: "#d32f2f", light: "#ef5350", dark: "#c62828" },
        warning: lightWarning,
        info: { main: "#0288d1", light: "#03a9f4", dark: "#01579b" },
        success: { main: "#2e7d32", light: "#4caf50", dark: "#1b5e20" },
        background: { default: "#f4f6f8", paper: "#ffffff" },
        surface: { inset: "#f1f3f5", track: "#e0e0e0" },
        status: lightStatus,
        inverted: {
            main: "#000000",
            contrastText: "#ffffff",
        },
        internal: {
            light: "#EFF6FF",
            main: "#BFDBFE",
            dark: "#3B82F6",
            contrastText: "#ffffff",
        },
        private: {
            light: "#FFFBEB",
            main: "#FDE68A",
            dark: "#F89E0B",
            contrastText: "#ffffff",
        },
    },
});

const darkPalette = {
    mode: "dark",
    primary: { main: "#90caf9", light: "#e3f2fd", dark: "#42a5f5", contrastText: "rgba(0,0,0,0.87)" },
    secondary: { main: "#4db6ac", light: "#80cbc4", dark: "#00897b", contrastText: "rgba(0,0,0,0.87)" },
    error: { main: "#f44336", light: "#e57373", dark: "#d32f2f" },
    warning: darkWarning,
    info: { main: "#29b6f6", light: "#4fc3f7", dark: "#0288d1" },
    success: { main: "#66bb6a", light: "#81c784", dark: "#388e3c" },
    background: { default: "#121212", paper: "#1e1e1e" },
    surface: { inset: "#262626", track: "#3a3a3a" },
    status: darkStatus,
    inverted: {
        main: "#ffffff",
        contrastText: "#000000",
    },
    internal: {
        light: "#13263b",
        main: "#3b82f6",
        dark: "#90caf9",
        contrastText: "#ffffff",
    },
    private: {
        light: "#332810",
        main: "#f5b820",
        dark: "#fbbf24",
        contrastText: "#ffffff",
    },
};

const darkBaseTheme = createTheme({ ...base, palette: { ...darkPalette } });

export const darkTheme = createTheme(darkBaseTheme, {
    shadows: darkBaseTheme.shadows.map((s, i) => (i === 0 ? s : `${s}, 0 0 0 1px rgba(255,255,255,0.08)`)),
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                a: {
                    color: darkBaseTheme.palette.primary.main,
                },
            },
        },
    },
});
